import { useCallback, useEffect, useState } from 'react';
import { vehicles as vehiclesApi } from '../api/vehicles';
import { DRAWINGS_AVAILABLE, drawingFileName, vehicleKey } from '../components/maps/vehicleSprites';

/**
 * The vehicle types the office keeps (`api/vehicles.js`), for a screen that
 * offers them: the client's vehicle on the parcel screen, the rider's on
 * registration. While the list is asked for, `vehicles` is empty; when it
 * cannot be read, `failed` is true and `retry` asks again. A screen never
 * falls back to the old three: the office may have switched one off.
 *
 * `source` is asked again whenever it changes, so it is made once (the app's
 * own `vehicles`, or a fake held outside the render): a new object on every
 * render asks, and renders, without end.
 *
 * @returns {{ vehicles: { key: string, label: string, drawing: string, serviceType: string }[], loading: boolean, failed: boolean, retry: () => void }}
 */
export function useVehicleTypes(source = vehiclesApi) {
  const [state, setState] = useState({ vehicles: [], loading: true, failed: false });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let gone = false;
    setState((s) => ({ ...s, loading: true, failed: false }));
    source.list().then(
      (list) => !gone && setState({ vehicles: list, loading: false, failed: false }),
      () => !gone && setState({ vehicles: [], loading: false, failed: true }),
    );

    return () => {
      gone = true;
    };
  }, [source, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { ...state, retry };
}

/**
 * The drawing for a vehicle a screen holds: the API's key (`van`) or the words
 * a session or a rider card carries ("Tuk-Tuk"), through the office's list.
 * Before the list is read, the old three's words still draw; anything else is
 * a motorbike, as maps have always drawn an unknown vehicle.
 */
export function drawingFor(value, vehicles = []) {
  const words = String(value ?? '').trim().toLowerCase();
  const type = vehicles.find((v) => v.key === value || v.key === words || v.label.toLowerCase() === words);
  if (type && DRAWINGS_AVAILABLE.includes(type.drawing)) return type.drawing;

  return vehicleKey(value) ?? 'motorbike';
}

/** The old three's words, for a key the office's list has not answered for (yet). */
const OLD_WORDS = { motorbike: 'Motorbike', tuk_tuk: 'Tuk-Tuk', car: 'Car' };

/** The words for a vehicle the API names by its key: the office's label, else what the screen was given. */
export function labelFor(value, vehicles = []) {
  const type = vehicles.find((v) => v.key === value);

  return type?.label ?? OLD_WORDS[value] ?? (value ? String(value) : '');
}

/** A vehicle drawing's file in `public/assets/vehicles/`; a drawing InGo does not have is the plain parcel. */
export function vehicleDrawingUrl(drawing) {
  const name = DRAWINGS_AVAILABLE.includes(drawing) ? drawing : 'parcel';

  return `${process.env.PUBLIC_URL || ''}/assets/vehicles/${drawingFileName(name)}`;
}
